import { useState } from "react";
import { toast } from "sonner";
import { Wand2, Loader2, Sparkles, ExternalLink } from "lucide-react";
import { generateBlog } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BlogBuilder = () => {
  const [topic, setTopic] = useState("");
  const [loading, setLoading] = useState(false);
  const [last, setLast] = useState(null);

  const run = async (useTopic) => {
    setLoading(true);
    setLast(null);
    try {
      const blog = await generateBlog(useTopic ? topic.trim() : "");
      setLast(blog);
      toast.success("Article generated & published!", {
        description: blog.title,
        action: { label: "View", onClick: () => window.open(`/blog/${blog.slug}`, "_blank") },
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-blog-builder" className="max-w-2xl">
      <div className="flex items-center gap-2">
        <Wand2 className="h-6 w-6 text-primary" />
        <h1 className="font-heading text-4xl text-secondary">AI SEO Content Generator</h1>
      </div>
      <p className="text-muted-foreground mt-1">Generate a fully SEO-optimized, published blog article in one click — powered by Gemini.</p>

      <div className="mt-8 bg-white border border-border rounded-xl p-6 space-y-5">
        <div>
          <Label className="text-sm">Enter Pooja/Ritual Topic (Optional)</Label>
          <Input
            data-testid="blog-topic-input"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="e.g., Narayan Bali Pooja in Gokarna"
            className="mt-1"
            disabled={loading}
          />
          <p className="text-xs text-muted-foreground mt-1">Leave blank to let AI pick a high-search-intent topic.</p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            data-testid="generate-publish-btn"
            onClick={() => run(true)}
            disabled={loading || !topic.trim()}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 font-semibold text-white hover:bg-[#CC5204] disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Generate &amp; Publish Article
          </button>
          <button
            data-testid="auto-generate-btn"
            onClick={() => run(false)}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-white px-6 py-3 font-semibold text-secondary hover:border-primary disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Auto-Select Topic &amp; Generate
          </button>
        </div>

        {loading && (
          <p data-testid="blog-loading" className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Writing your article… this can take ~20–40 seconds.
          </p>
        )}

        {last && (
          <div data-testid="blog-result" className="border border-gold/40 bg-cream rounded-lg p-4">
            <p className="text-xs uppercase tracking-wide text-primary font-semibold">{last.category}</p>
            <h3 className="font-heading text-xl text-secondary mt-1">{last.title}</h3>
            <p className="text-sm text-muted-foreground mt-1">{last.meta_description}</p>
            {last.keywords && <p className="text-xs text-muted-foreground mt-2"><span className="font-semibold text-secondary">Keywords:</span> {last.keywords}</p>}
            <a
              href={`/blog/${last.slug}`}
              target="_blank"
              rel="noreferrer"
              data-testid="view-published-post"
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
            >
              View published post <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogBuilder;
